import React, { useState, useMemo } from "react";
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Tooltip,
  Stack,
  Divider,
} from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { Fade, Slide } from "react-awesome-reveal";

export default function HeroPrices1() {
  // Valores por día (USD)
  const PRICE_PER_PROPERTY = 1.2;
  const PRICE_PER_ROOM = 0.11;
  const PRICE_PER_OTA = 0.11;

  // Defaults sugeridos
  const [properties, setProperties] = useState(1);
  const [rooms, setRooms] = useState(7);
  const [otas, setOtas] = useState(5);
  const [daysInMonth, setDaysInMonth] = useState(30); // elegimos 30 para estimación clara

  const formatUSD = (n) =>
    n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 });

  const dailyBase = useMemo(() => {
    const p = Math.max(1, Number(properties) || 1);
    const r = Math.max(0, Number(rooms) || 0);
    const o = Math.max(0, Number(otas) || 0);
    return p * PRICE_PER_PROPERTY + r * PRICE_PER_ROOM + o * PRICE_PER_OTA;
  }, [properties, rooms, otas]);

  const monthlyBase = useMemo(() => dailyBase * Math.max(1, Number(daysInMonth) || 30), [dailyBase, daysInMonth]);

  return (
    <Box sx={{ py: { xs: 8, md: 10 }, backgroundColor: "#f9f9f9" }}>
      <Container maxWidth="lg">
        {/* Cabecera */}
        <Box sx={{ textAlign: "center", mb: { xs: 5, md: 7 } }}>
          <Slide direction="up" triggerOnce>
            <Typography
              variant="h3"
              fontWeight="bold"
              gutterBottom
              sx={{ color: "#262626", fontFamily: "'Red Hat Display', sans-serif" }}
            >
              Un plan flexible. Pagás solo por lo que usás.
            </Typography>
          </Slide>
          <Fade triggerOnce delay={120}>
            <Typography
              variant="subtitle1"
              sx={{ color: "#666", fontFamily: "'Merriweather Sans', sans-serif" }}
            >
              USD <b>1.20</b> por propiedad/día + USD <b>0.11</b> por habitación/día + USD <b>0.11</b> por OTA/día
              &nbsp;·&nbsp; Facturación mensual &nbsp;+&nbsp; <b>3%</b> de comisión por reservas cobradas.
            </Typography>
          </Fade>
        </Box>

        {/* Calculadora + Resultados */}
        <Grid container spacing={4} justifyContent="center" alignItems="stretch">
          {/* Calculadora */}
          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 3, md: 4 },
                borderRadius: 3,
                backgroundColor: "#fff",
                border: "1px solid #e0e0e0",
                boxShadow: "0 0 24px rgba(255,255,255,0.85)",
                height: "100%",
              }}
            >
              <Typography
                variant="h6"
                fontWeight="bold"
                sx={{ color: "#903AF2", mb: 2, fontFamily: "'Red Hat Display', sans-serif" }}
              >
                Calculá tu costo mensual estimado
              </Typography>

              <Stack spacing={2.2}>
                <TextField
                  label="Propiedades"
                  type="number"
                  value={properties}
                  onChange={(e) => setProperties(e.target.value)}
                  inputProps={{ min: 1 }}
                  helperText="Mínimo 1 propiedad"
                  fullWidth
                />

                <TextField
                  label="Habitaciones totales"
                  type="number"
                  value={rooms}
                  onChange={(e) => setRooms(e.target.value)}
                  inputProps={{ min: 0 }}
                  fullWidth
                />

                <TextField
                  label="Canales OTA conectados"
                  type="number"
                  value={otas}
                  onChange={(e) => setOtas(e.target.value)}
                  inputProps={{ min: 0 }}
                  fullWidth
                />

                <TextField
                  label="Días del mes (estimación)"
                  type="number"
                  value={daysInMonth}
                  onChange={(e) => setDaysInMonth(e.target.value)}
                  inputProps={{ min: 28, max: 31 }}
                  fullWidth
                />

                <Divider sx={{ my: 1 }} />

                <Stack spacing={1.2}>
                  <RowPrice
                    label="Costo por día (base)"
                    value={formatUSD(dailyBase)}
                    hint={`= ${formatUSD(PRICE_PER_PROPERTY)} x propiedad + ${formatUSD(
                      PRICE_PER_ROOM
                    )} x habitación + ${formatUSD(PRICE_PER_OTA)} x OTA`}
                  />
                  <RowPrice
                    label={`Costo mensual (x ${daysInMonth} días)`}
                    value={formatUSD(monthlyBase)}
                  />
                </Stack>

                <Box sx={{ mt: 1 }}>
                  <Tooltip
                    title="Se aplica sobre reservas cobradas a través del sistema (no incluido en el valor base)."
                    arrow
                  >
                    <Typography variant="caption" sx={{ color: "#666" }}>
                      + 3% de comisión por reservas cobradas
                      <HelpOutlineIcon sx={{ fontSize: 16, ml: 0.5, verticalAlign: "middle" }} />
                    </Typography>
                  </Tooltip>
                </Box>

                <Button
                  variant="contained"
                  fullWidth
                  sx={{
                    mt: 1,
                    backgroundColor: "#903AF2",
                    color: "#fff",
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: "bold",
                    fontFamily: "'Red Hat Display', sans-serif",
                    "&:hover": { backgroundColor: "#7d2bd4" },
                  }}
                  onClick={() => (window.location.href = "https://wa.me/542604206967")}
                >
                  Quiero activar Ubika
                </Button>
              </Stack>
            </Paper>
          </Grid>

          {/* Beneficios / Qué incluye */}
          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 3, md: 4 },
                borderRadius: 3,
                backgroundColor: "#fff",
                border: "1px solid #e0e0e0",
                boxShadow: "0 0 24px rgba(255,255,255,0.85)",
                height: "100%",
              }}
            >
              <Typography
                variant="h6"
                fontWeight="bold"
                sx={{ color: "#903AF2", mb: 2, fontFamily: "'Red Hat Display', sans-serif" }}
              >
                Incluye todo lo clave para vender y operar
              </Typography>

              <Stack spacing={1.2} sx={{ color: "#262626", fontFamily: "'Merriweather Sans', sans-serif" }}>
                <Bullet>Motor de reservas directo (sin comisiones externas)</Bullet>
                <Bullet>Gestión de reservas en un solo calendario</Bullet>
                <Bullet>Conexión con Booking, Airbnb, Hostelworld y más</Bullet>
                <Bullet>Pagos online con links/QR y conciliación</Bullet>
                <Bullet>Reportes de ocupación e ingresos</Bullet>
                <Bullet>Soporte en español y onboarding inicial</Bullet>
              </Stack>

              <Divider sx={{ my: 3 }} />

              <Fade triggerOnce>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: "rgba(144,58,242,0.05)",
                    borderColor: "rgba(144,58,242,0.25)",
                  }}
                >
                  <Typography variant="body2" sx={{ color: "#555" }}>
                    <b>Transparente y escalable:</b> si sumás habitaciones u OTAs, el costo se ajusta automáticamente.
                    Sin letras chicas.
                  </Typography>
                </Paper>
              </Fade>
            </Paper>
          </Grid>
        </Grid>

        {/* Notas aclaratorias */}
        <Box sx={{ mt: 8, textAlign: "center" }}>
          <Typography
            variant="body2"
            sx={{ color: "#999", mb: 1, fontFamily: "'Merriweather Sans', sans-serif" }}
          >
            * Estimación sobre un mes de {daysInMonth} días. El valor final se calcula según uso real.
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: "#999", fontFamily: "'Merriweather Sans', sans-serif" }}
          >
            * La comisión del 3% se aplica a reservas cobradas a través del sistema.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}

/* --- Subcomponentes de apoyo --- */
function RowPrice({ label, value, hint }) {
  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
      <Typography sx={{ color: "#444", fontFamily: "'Merriweather Sans', sans-serif" }}>
        {label}
      </Typography>
      <Stack alignItems="flex-end">
        <Typography
          sx={{ color: "#262626", fontWeight: 800, fontFamily: "'Red Hat Display', sans-serif" }}
        >
          {value}
        </Typography>
        {hint && (
          <Typography variant="caption" sx={{ color: "#777" }}>
            {hint}
          </Typography>
        )}
      </Stack>
    </Stack>
  );
}

function Bullet({ children }) {
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <CheckCircleIcon sx={{ color: "#903AF2", fontSize: 18 }} />
      <Typography variant="body2">{children}</Typography>
    </Stack>
  );
}
